import React, { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { Tag } from "primereact/tag";
import { useNavigate } from "react-router-dom";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { deleteReview } from "../../store/AsyncMethods/ReviewsMethod";

export default function ManageReviewsTable({ reviews = [], loading = false }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    customer: { value: null, matchMode: FilterMatchMode.CONTAINS },
    product: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
  });

  // Transform reviews data to match table structure
  const transformedReviews = reviews.map((review) => ({
    id: review.id,
    customer: review.customer_name || review.customer,
    product: review.product_name || review.product,
    rating: review.rating,
    comment: review.comment || review.review_text,
    status: review.status,
    date: review.created_at || review.date,
    helpful: review.helpful_votes || review.helpful || 0,
  }));

  const onGlobalFilterChange = (event) => {
    const value = event.target.value;
    let _filters = { ...filters };

    _filters["global"].value = value;

    setFilters(_filters);
  };

  const renderHeader = () => {
    const value = filters["global"]?.value || "";

    return (
      <div className="flex justify-between">
        <h5 className="m-0">Reviews</h5>
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={value}
            onChange={onGlobalFilterChange}
            placeholder="Search Reviews..."
          />
        </span>
      </div>
    );
  };

  const statusBodyTemplate = (rowData) => {
    const getSeverity = (status) => {
      switch (status) {
        case "approved":
          return "success";
        case "pending":
          return "warning";
        case "rejected":
          return "danger";
        default:
          return null;
      }
    };

    return (
      <Tag
        value={rowData.status}
        severity={getSeverity(rowData.status)}
        className="capitalize"
      />
    );
  };

  const ratingBodyTemplate = (rowData) => {
    return (
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, index) => (
          <i
            key={index}
            className={`pi ${
              index < rowData.rating ? "pi-star-fill text-yellow-400" : "pi-star text-gray-300"
            }`}
          />
        ))}
        <span className="ml-2 text-sm text-gray-600">({rowData.rating}/5)</span>
      </div>
    );
  };

  const commentBodyTemplate = (rowData) => {
    return (
      <div className="max-w-xs">
        <p className="text-sm text-gray-700 line-clamp-2">
          {rowData.comment}
        </p>
      </div>
    );
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-eye"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => navigate(`/view-review/${rowData.id}`)}
          tooltip="View Review"
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-sm p-button-success"
          onClick={() => navigate(`/edit-review/${rowData.id}`)}
          tooltip="Edit Review"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-sm p-button-danger"
          onClick={() => confirmDelete(rowData)}
          tooltip="Delete Review"
        />
      </div>
    );
  };

  const confirmDelete = async (review) => {
    confirmDialog({
      message: `Are you sure you want to delete the review from ${review.customer}?`,
      header: "Delete Confirmation",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        const success = await dispatch(deleteReview(review.id));
        if (success) {
          toast.success("Review deleted successfully");
        }
      },
    });
  };

  const header = renderHeader();

  return (
    <div className="card">
      <ConfirmDialog />
      <DataTable
        value={transformedReviews}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        tableStyle={{ minWidth: "50rem" }}
        filters={filters}
        filterDisplay="menu"
        globalFilterFields={["customer", "product", "comment"]}
        header={header}
        emptyMessage="No reviews found."
        responsiveLayout="scroll"
        loading={loading}
      >
        <Column field="id" header="ID" sortable style={{ width: "5%" }} />
        <Column field="customer" header="Customer" sortable filter style={{ width: "12%" }} />
        <Column field="product" header="Product" sortable filter style={{ width: "15%" }} />
        <Column field="rating" header="Rating" body={ratingBodyTemplate} sortable style={{ width: "15%" }} />
        <Column field="comment" header="Comment" body={commentBodyTemplate} style={{ width: "25%" }} />
        <Column field="status" header="Status" body={statusBodyTemplate} sortable filter style={{ width: "10%" }} />
        <Column field="date" header="Date" sortable style={{ width: "10%" }} />
        <Column field="helpful" header="Helpful" sortable style={{ width: "8%" }} />
        <Column header="Actions" body={actionBodyTemplate} style={{ width: "10%" }} />
      </DataTable>
    </div>
  );
} 